import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Container } from '../StyledComponents/StyledComponents';
import FeaturedArticle from '../Article/FeaturedArticle';
import { ArticleAd } from '../AdSense/AdComponents';
import Posts from '../../posts/Posts';

const MorePostsSection = styled.section`
    margin: 4rem 0;
`;

const SectionTitle = styled.h2`
    font-family: 'Playfair Display', serif;
    font-size: 2.5rem;
    font-weight: 700;
    color: ${props => props.theme.body.headers};
    text-align: center;
    margin-bottom: 3rem;
    
    @media (max-width: ${props => props.theme.queries.mobile}) {
        font-size: 2rem;
    }
`;

const PostsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
    margin-top: 2rem;
    
    @media (max-width: ${props => props.theme.queries.tablet}) {
        grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: ${props => props.theme.queries.mobile}) {
        grid-template-columns: 1fr;
        gap: 1.5rem;
    }
`;

const AdContainer = styled.div`
    grid-column: 1 / -1;
    display: flex;
    justify-content: center;
    margin: 1rem 0;
`;

const LoadMoreButton = styled.button`
    font-family: ${props => props.theme.typography.font};
    background: ${props => props.theme.body.headers};
    color: ${props => props.theme.body.background};
    border: none;
    padding: 1rem 2rem;
    font-size: 1rem;
    font-weight: 400;
    border-radius: 4px;
    cursor: pointer;
    margin: 3rem auto 0;
    display: block;
    transition: all 0.3s ease;
    
    &:hover {
        opacity: 0.8;
        transform: translateY(-2px);
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
    }
`;

const LoadingIndicator = styled.div`
    text-align: center;
    margin: 2rem 0;
    font-family: ${props => props.theme.typography.font};
    color: ${props => props.theme.body.text};
    font-style: italic;
`;

// Función para aleatorizar array
const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

const MoreKnowledge = ({ currentRoute, initialCount = 9 }) => {
    const [displayedPosts, setDisplayedPosts] = useState([]);
    const [allShuffledPosts, setAllShuffledPosts] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const observerRef = useRef();

    // Inicializar posts aleatorios
    useEffect(() => {
        const otherPosts = Posts.filter(post => post.route !== currentRoute);
        const shuffled = shuffleArray(otherPosts);
        setAllShuffledPosts(shuffled);
        
        // Cargar los primeros posts
        const initialPosts = shuffled.slice(0, initialCount);
        setDisplayedPosts(initialPosts);
        setCurrentIndex(initialCount);
        setHasMore(shuffled.length > initialCount);
    }, [currentRoute, initialCount]);

    // Función para cargar más posts
    const loadMorePosts = useCallback(() => {
        if (loading || !hasMore) return;
        
        setLoading(true);
        
        // Simular un pequeño delay para mejor UX
        setTimeout(() => {
            const nextPosts = allShuffledPosts.slice(currentIndex, currentIndex + initialCount);
            
            if (nextPosts.length > 0) {
                setDisplayedPosts(prev => [...prev, ...nextPosts]);
                setCurrentIndex(prev => prev + nextPosts.length);
                
                // Check if there are more posts
                if (currentIndex + nextPosts.length >= allShuffledPosts.length) {
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
            
            setLoading(false);
        }, 500);
    }, [loading, hasMore, allShuffledPosts, currentIndex, initialCount]);

    // Intersection Observer para scroll infinito
    const lastPostElementRef = useCallback(node => {
        if (loading) return;
        if (observerRef.current) observerRef.current.disconnect();
        
        observerRef.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadMorePosts();
            }
        }, {
            rootMargin: '100px' // Trigger 100px before reaching the element
        });
        
        if (node) observerRef.current.observe(node);
    }, [loading, hasMore, loadMorePosts]);

    // Renderizar posts con anuncios cada 8 entradas
    const renderPostsWithAds = () => {
        const items = [];
        
        displayedPosts.forEach((post, index) => {
            // Agregar el post
            items.push(
                <div
                    key={`post-${post.route}-${index}`}
                    ref={index === displayedPosts.length - 1 ? lastPostElementRef : null}
                >
                    <FeaturedArticle
                        post={post}
                        featured={false}
                    />
                </div>
            );
            
            // Agregar anuncio cada 8 entradas (posiciones 7, 15, 23, etc.)
            if ((index + 1) % 8 === 0 && index < displayedPosts.length - 1) {
                items.push(
                    <AdContainer key={`ad-${index}`}>
                        <ArticleAd />
                    </AdContainer>
                );
            }
        });
        
        return items;
    };

    return (
        <MorePostsSection>
            <Container>
                <SectionTitle>Más conocimiento inútil</SectionTitle>
                <PostsGrid>
                    {renderPostsWithAds()}
                </PostsGrid>
                
                {loading && (
                    <LoadingIndicator>
                        Cargando más conocimiento inútil...
                    </LoadingIndicator>
                )}
                
                {!hasMore && displayedPosts.length > initialCount && (
                    <LoadingIndicator>
                        Has llegado al final del conocimiento inútil disponible 🤓
                    </LoadingIndicator>
                )}
            </Container>
        </MorePostsSection>
    );
};

export default MoreKnowledge;